import React, { useRef, useState } from "react";
import { RecBar, RecIcon } from "../../materials/MaterialRecordParts";
import { createProjectTask, updateProjectTask } from "../../../helpers/tasks/projectTasks";
import { analyzeBrief, EmptyBriefError } from "../../../taskIntelligence/analyzeBrief";
import { draftToTaskInput, TASK_ACTION } from "../../../taskIntelligence/taskDraftModel";
import { TaskBuilderReviewScreen } from "./TaskBuilderReviewScreen";
import { useDictation } from "../../../speech/useDictation";
import { SpeechDebugPanel } from "../../../speech/SpeechDebugPanel";
import "./taskBuilder.css";

const PROCESSING_STEPS = [
  "Transcribing audio",
  "Understanding context",
  "Checking existing tasks",
  "Identifying work items",
  "Preparing recommendations",
];

function draftToUpdatePatch(draft) {
  return {
    title: draft.title,
    description: draft.description || "",
    priority: draft.priority || "",
    assignee_id: draft.assigneeId || "",
    due_date: draft.dueDate || "",
    location_id: draft.locationId || "",
  };
}

function InputStep({ briefText, setBriefText, source, setSource, onAnalyze, error }) {
  const [speechError, setSpeechError] = useState("");
  const latestText = useRef(briefText);
  latestText.current = briefText;
  // Shared dictation lifecycle (one live session, interim replaced, stale sessions detached).
  const dictation = useDictation({
    getText: () => latestText.current,
    onText: setBriefText,
    onError: () => setSpeechError("Recording stopped. What was captured is kept below — edit it or try again."),
    // Silence may end a browser recognition instance. The user session stays up until Stop.
    holdSession: true,
  });
  const { recording } = dictation;

  function startRecording() {
    setSpeechError("");
    if (!dictation.start()) {
      setSpeechError("This browser has no speech transcription. Type your brief instead.");
      setSource("text");
    }
  }

  function stopRecording() {
    dictation.stop();
  }

  function handleEdit(value) {
    dictation.takeOver();
    setBriefText(value);
  }

  return (
    <div className="tbl-body">
      <p className="tbl-intro">Tell me what needs to get done. I'll turn it into a task list.</p>

      <div className="tbl-tabs" role="tablist" aria-label="Brief input method">
        <button
          type="button"
          role="tab"
          aria-selected={source === "voice"}
          className={`tbl-tab${source === "voice" ? " is-on" : ""}`}
          onClick={() => setSource("voice")}
        >
          <RecIcon name="mic" /> Voice
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={source === "text"}
          className={`tbl-tab${source === "text" ? " is-on" : ""}`}
          onClick={() => {
            stopRecording();
            setSource("text");
          }}
        >
          <RecIcon name="doc" /> Text
        </button>
      </div>

      {source === "voice" ? (
        <div className="tbl-voice">
          <button
            type="button"
            className={`tbl-mic${recording ? " is-recording" : ""}`}
            onClick={recording ? stopRecording : startRecording}
            aria-pressed={recording}
            aria-label={recording ? "Stop voice input" : "Begin voice input"}
          >
            <RecIcon name="mic" />
          </button>
          <div className="tbl-voice__label">
            {recording ? (
              <>
                Listening…
                <span className="tbl-voice__stop">Tap to stop.</span>
              </>
            ) : (
              "Tap to begin voice input."
            )}
          </div>
          {speechError ? <div className="tbl-hint">{speechError}</div> : null}
          <SpeechDebugPanel debug={dictation.debug} />
        </div>
      ) : null}

      <label className="tbl-fieldlabel" htmlFor="tbl-brief">
        {source === "voice" ? "Transcript (edit if needed)" : "Your brief"}
      </label>
      <textarea
        id="tbl-brief"
        className="tbl-textarea"
        value={briefText}
        onChange={(e) => handleEdit(e.target.value)}
        placeholder="e.g. Tomorrow we're redoing the shower. Tear out the old tile, check the framing, and install the new backer…"
        rows={7}
      />
      <div className="tbl-hint">Be as detailed as you like. Mention people, dates, locations, and any important details.</div>

      {error ? (
        <div className="rec-banner" role="alert">
          {error}
        </div>
      ) : null}

      <button type="button" className="tbl-primary" disabled={!briefText.trim()} onClick={onAnalyze}>
        <RecIcon name="select" /> Analyze Brief
      </button>
    </div>
  );
}

function ProcessingStep({ doneIndex }) {
  return (
    <div className="tbl-body tbl-processing">
      <div className="tbl-processing__ring" aria-hidden="true">
        <RecIcon name="select" />
      </div>
      <h2 className="tbl-processing__title">Analyzing your brief…</h2>
      <ul className="tbl-steps">
        {PROCESSING_STEPS.map((label, index) => (
          <li key={label} className={`tbl-step${index <= doneIndex ? " is-done" : index === doneIndex + 1 ? " is-active" : ""}`}>
            {index <= doneIndex ? <RecIcon name="check" /> : <span className="tbl-step__dot" />}
            {label}
          </li>
        ))}
      </ul>
      <div className="tbl-hint tbl-processing__note">This usually takes a few seconds.</div>
    </div>
  );
}

/**
 * "Build Tasks with Ashley" — Input → Processing → Review. The only writes
 * happen at the very end, when the user presses Create: ordinary
 * createProjectTask / updateProjectTask calls, identical to the manual path.
 */
export function TaskBuilderFlow({ actor, projectId, projectName, locations = [], directReportIds, onBack, onTasksCreated }) {
  const [step, setStep] = useState("input");
  const [briefText, setBriefText] = useState("");
  const [source, setSource] = useState("voice");
  const [error, setError] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [processingDone, setProcessingDone] = useState(-1);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  async function runAnalysis() {
    setError("");
    setStep("processing");
    setProcessingDone(-1);
    const timer = setInterval(() => {
      setProcessingDone((prev) => (prev < PROCESSING_STEPS.length - 2 ? prev + 1 : prev));
    }, 550);
    try {
      const result = await analyzeBrief(actor, { projectId, projectName, briefText, source, locations, directReportIds });
      clearInterval(timer);
      setProcessingDone(PROCESSING_STEPS.length - 1);
      setAnalysis(result);
      setDrafts(result.drafts);
      setStep("review");
    } catch (err) {
      clearInterval(timer);
      if (err instanceof EmptyBriefError) {
        setError(err.message);
      } else {
        setError(err?.message || "Could not analyze that brief. Try again.");
      }
      setStep("input");
    }
  }

  async function createSelected(selectedDrafts) {
    setCreating(true);
    setCreateError("");
    const failed = [];
    let createdCount = 0;
    for (const draft of selectedDrafts) {
      try {
        if (draft.action === TASK_ACTION.UPDATE) {
          await updateProjectTask(actor, draft.existingTaskId, draftToUpdatePatch(draft));
        } else {
          await createProjectTask(actor, projectId, draftToTaskInput(draft));
        }
        createdCount += 1;
      } catch (err) {
        failed.push({ draft, message: err?.message || "Could not save this task." });
      }
    }
    setCreating(false);
    if (failed.length) {
      setDrafts((prev) => prev.filter((d) => failed.some((f) => f.draft.id === d.id)));
      setCreateError(
        `${createdCount ? `${createdCount} task${createdCount === 1 ? "" : "s"} created. ` : ""}${failed.length} could not be saved — fix and try again.`,
      );
      return;
    }
    onTasksCreated?.(createdCount);
    onBack?.();
  }

  if (step === "input") {
    return (
      <div className="screen nx-screen rec-page tbl-screen" data-testid="task-builder-input">
        <RecBar title="Build Tasks with Ashley" onBack={onBack} />
        <InputStep
          briefText={briefText}
          setBriefText={setBriefText}
          source={source}
          setSource={setSource}
          onAnalyze={runAnalysis}
          error={error}
        />
      </div>
    );
  }

  if (step === "processing") {
    return (
      <div className="screen nx-screen rec-page tbl-screen" data-testid="task-builder-processing">
        <RecBar title="Build Tasks with Ashley" onBack={() => setStep("input")} />
        <ProcessingStep doneIndex={processingDone} />
      </div>
    );
  }

  return (
    <TaskBuilderReviewScreen
      projectName={projectName}
      drafts={drafts}
      setDrafts={setDrafts}
      noAction={analysis?.noAction || []}
      notes={analysis?.notes || []}
      existingTasksById={analysis?.existingTasksById || new Map()}
      members={analysis?.context?.members || []}
      locations={analysis?.context?.locations || []}
      creating={creating}
      createError={createError}
      onCreate={createSelected}
      onBack={() => setStep("input")}
    />
  );
}
